import apiClient from './api.js';

export const getUserById = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please login.');
    }
    const response = await apiClient.get(`/api/users/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    throw error;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/api/users/profile/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.put('/api/users/profile', profileData, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const getUserSubscriptionStatus = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get(`/api/users/subscription-status/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user subscription status:', error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/api/auth/users/', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

export const listUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.get('/api/auth/list-users', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching list users:', error);
    throw error;
  }
};

export const regeneratePrivateCode = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await apiClient.post(`/api/auth/users/${userId}/regenerate-private-code`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error regenerating private code:', error);
    throw error;
  }
};

export const fetchPrivateCodeFromAPI = async (userId) => {
  try {
    const userData = await getUserById(userId);
    return {
      success: true,
      privateCode: userData.privateCode,
      message: 'Private code fetched successfully'
    };
  } catch (error) {
    console.error('Error fetching private code from API:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch private code',
      message: 'Failed to fetch private code from API'
    };
  }
};

export const sendPrivateCode = async (userId, privateCode) => {
  try {
    const token = localStorage.getItem('token');
    // Send private code for verification or other purposes
    // Since backend doesn't have direct private code update endpoint,
    // this could be used for verification or logging purposes
    const response = await apiClient.post(`/api/auth/users/${userId}/verify-private-code`, {
      privateCode: privateCode
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error sending private code:', error);
    // If the endpoint doesn't exist, return a fallback response
    if (error.response && error.response.status === 404) {
      return {
        success: true,
        message: 'Private code sent (endpoint not implemented yet)',
        privateCode: privateCode
      };
    }
    throw error;
  }
};

// Test data for registration
export const testRegistrationData = {
  firstName: "SEIFEDDINE",
  lastName: "JAMAI",
  email: "seifeddinejamai8@gmail.com",
  password: "Seif2025$",
  confirmPassword: "Seif2025$"
};

// Test function to register user with test data
export const testRegisterUser = async () => {
  console.log('Testing registration with data:', testRegistrationData);
  return await registerUser(testRegistrationData);
};

// Register new user
export const registerUser = async (userData) => {
  try {
    const { firstName, lastName, email, password, confirmPassword, referralId } = userData;

    // Basic validation
    if (!firstName?.trim() || !lastName?.trim() || !email?.trim() || !password || !confirmPassword) {
      throw new Error('All fields are required');
    }

    if (password !== confirmPassword) {
      throw new Error('Passwords do not match');
    }

    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }

    // Prepare data for backend
    const registrationData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.toLowerCase().trim(),
      password,
      confirmPassword,
      referralId: referralId || null
    };

    // Call backend API to create user
    const response = await apiClient.post('/api/auth/register', registrationData);

    console.log('User registered successfully:', response.data.user.email);

    // Return backend response
    return {
      success: true,
      user: response.data.user,
      token: response.data.token,
      message: 'Registration successful'
    };
  } catch (error) {
    console.error('Registration error:', error);

    // Handle different error types
    if (error.response) {
      // Backend returned an error
      const errorMessage = error.response.data.message || 'Registration failed';
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    } else if (error.request) {
      // Network error
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        message: 'Network error. Please check your connection.'
      };
    } else {
      // Other error
      return {
        success: false,
        error: error.message || 'Registration failed. Please try again.',
        message: error.message || 'Registration failed. Please try again.'
      };
    }
  }
};
